import React, { useState, useEffect, useRef } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext"; // New Content Context
import { FaHeart, FaRegHeart, FaLock } from "react-icons/fa"; // Add FaLock import
import {
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePassword } from "../context/PasswordContext";
import { useTranslation } from "../hooks/useTranslation";

const SavedMunis = () => {
  const { series, liveEpisodes } = useContent(); // Fetching series & live episodes
  const { user } = UserAuth();
  const { checkSeriesAccess } = usePassword(); // Add this hook
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const navigate = useNavigate();
  const [protectedSeries, setProtectedSeries] = useState({});

  const [savedSeries, setSavedSeries] = useState([]);

  useEffect(() => {
    if (user?.uid) {
      const fetchSavedSeries = async () => {
        setLoading(true);

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const savedSeriesIds = userData.savedSeries?.map((s) => s.id) || [];

          // Filter out series that exist in the main series list
          const filteredSeries = series.filter((s) =>
            savedSeriesIds.includes(s.id),
          );

          // If there are any missing series, update Firestore
          if (savedSeriesIds.length !== filteredSeries.length) {
            const validSavedSeries = userData.savedSeries.filter((saved) =>
              filteredSeries.some((s) => s.id === saved.id),
            );

            // Update Firestore with cleaned list
            await updateDoc(userDocRef, {
              savedSeries: validSavedSeries,
            });
          }

          setSavedSeries(filteredSeries);
        }

        setLoading(false);
      };

      fetchSavedSeries();
    }
  }, [user, series]);

  useEffect(() => {
    if (savedSeries.length >= 0) {
      setLoading(false);
    }
  }, [savedSeries]);

  const slideLeft = () => {
    if (sliderRef.current) sliderRef.current.scrollLeft -= 500;
  };

  const slideRight = () => {
    if (sliderRef.current) sliderRef.current.scrollLeft += 500;
  };

  useEffect(() => {
    if (sliderRef.current) {
      setIsOverflowing(
        sliderRef.current.scrollWidth > sliderRef.current.clientWidth,
      );
    }
  }, [savedSeries]);

  // Save a series to user's list
  const saveSeries = async (seriesItem) => {
    if (!user?.uid) {
      alert(t("pleaseLoginToSaveSeries"));
      return;
    }

    const userDocRef = doc(db, "users", user.uid);

    try {
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        let updatedSavedSeries = userDocSnap.data().savedSeries || [];
        const existingSeries = updatedSavedSeries.find(
          (s) => s.id === seriesItem.id,
        );

        if (existingSeries) {
          // Remove from Firestore
          await updateDoc(userDocRef, {
            savedSeries: arrayRemove(existingSeries),
          });

          // Remove from local state
          setSavedSeries((prev) => prev.filter((s) => s.id !== seriesItem.id));
        } else {
          // Create new series object
          const newSeries = {
            id: seriesItem.id,
            title: seriesItem.title,
            thumbnail: seriesItem.thumbnail,
            linkUrl: seriesItem.linkUrl,
          };

          // Add to Firestore
          await updateDoc(userDocRef, {
            savedSeries: arrayUnion(newSeries),
          });

          // Add to local state
          setSavedSeries((prev) => [...prev, newSeries]); // 🔥 Instant UI update
        }
      } else {
        // If user doc doesn't exist, create it
        const newSeriesList = [
          {
            id: seriesItem.id,
            title: seriesItem.title,
            thumbnail: seriesItem.thumbnail,
            linkUrl: seriesItem.linkUrl,
          },
        ];
        await setDoc(userDocRef, { savedSeries: newSeriesList });

        // Set local state
        setSavedSeries(newSeriesList); // 🔥 Instant UI update
      }
    } catch (error) {
      console.error("Error updating saved series:", error);
    }
  };

  // Add effect to check password protection status
  useEffect(() => {
    let isMounted = true;

    const checkProtection = async () => {
      const protectionStatus = {};

      for (const seriesItem of savedSeries) {
        const linkUrl = `/series/${seriesItem.id}`;
        const { isProtected, isAuthorized } = await checkSeriesAccess(
          seriesItem.id,
          linkUrl,
          false,
        );

        if (isProtected) {
          protectionStatus[seriesItem.id] = {
            isProtected: true,
            hasAccess: isAuthorized,
          };
        }
      }

      if (isMounted) {
        setProtectedSeries(protectionStatus);
      }
    };

    if (savedSeries.length > 0) {
      checkProtection();
    }

    return () => {
      isMounted = false;
    };
  }, [savedSeries, checkSeriesAccess]);

  if (loading) {
    return <p>{t("loading")}</p>;
  }

  return (
    <div>
      <h2 className="text-white font-bold md:text-xl p-4">
        {t("myFavourites")}
      </h2>
      <div className="relative flex items-center group pl-2">
        {isOverflowing && (
          <MdChevronLeft
            onClick={slideLeft}
            className="bg-yellow-400 left-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
        <div
          id="slider"
          ref={sliderRef}
          className="w-full h-full overflow-x-scroll whitespace-nowrap scroll-smooth scrollbar-hide"
        >
          {!loading && savedSeries.length > 0 ? (
            savedSeries.map((seriesItem) => {
              // Check both series tag and episodes for live status
              const isLive =
                seriesItem.tags?.includes("live") ||
                liveEpisodes.some((ep) => ep.seriesId === seriesItem.id);
              const linkUrl = `/series/${seriesItem.id}`;
              const isProtected =
                protectedSeries[seriesItem.id]?.isProtected || false;
              const hasSeriesAccess =
                protectedSeries[seriesItem.id]?.hasAccess || false;
              const needsLock = isProtected && !hasSeriesAccess;

              return (
                <div
                  key={seriesItem.id}
                  className={`w-[160px] sm:w-[200px] md:w-[240px] lg:w-[280px] inline-block relative p-2`}
                >
                  {" "}
                  <>
                    <Link to={linkUrl}>
                      <div className="relative">
                        <img
                          className="w-full h-auto block rounded aspect-video"
                          src={seriesItem.thumbnail}
                          alt={seriesItem.title}
                        />

                        {/* Add Lock overlay for password protected content */}
                        {needsLock && (
                          <div className="absolute inset-0 bg-black/50 rounded flex flex-col justify-center items-center">
                            <FaLock className="text-yellow-400 text-2xl mb-2" />
                            <p className="text-white text-xs">
                              {t("passwordProtected")}
                            </p>
                          </div>
                        )}

                        {/* Standard hover overlay */}
                        <div className="absolute top-0 left-0 w-full h-full hover:bg-black/80 opacity-0 hover:opacity-100 text-white flex justify-center items-center rounded p-2">
                          <p className="text-white text-xs md:text-sm font-bold break-words text-center w-full max-w-full overflow-hidden">
                            {seriesItem.title}
                          </p>
                        </div>

                        {/* Live badge */}
                        {isLive && (
                          <div className="absolute top-2 right-2 z-30">
                            <p className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold">
                              LIVE
                            </p>
                          </div>
                        )}
                      </div>

                      <p className="whitespace-normal text-xs md:text-sm font-bold text-center w-full px-2 mt-2 min-h-[2.5rem] line-clamp-2">
                        <span className="text-white">{seriesItem.title}</span>
                      </p>
                    </Link>
                    <p
                      onClick={() => saveSeries(seriesItem)}
                      className="absolute top-4 left-4 cursor-pointer"
                    >
                      {savedSeries.some((s) => s.id === seriesItem.id) ? (
                        <FaHeart className="text-red-300" />
                      ) : (
                        <FaRegHeart className="text-red-300" />
                      )}
                    </p>
                  </>
                </div>
              );
            })
          ) : (
            <p className="text-white">{t("noSavedFavourites")}</p>
          )}
        </div>
        {isOverflowing && (
          <MdChevronRight
            onClick={slideRight}
            className="bg-yellow-400 right-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
      </div>
    </div>
  );
};

export default SavedMunis;
